import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

import { ReviewThreadService } from '../../services/review-thread.service';
import { StatusPillComponent } from '../../shared/status-pill/status-pill.component';

type ThreadStatus = 'OUTSTANDING' | 'RESOLVED';
type ThreadType = 'REVISION_REQUIRED' | 'QUESTION';

/**
 * CONNECT 2.0 Comments card — the PA (vendor) surface of the task review-thread
 * feature. Reads `proposal.reviewThreads` (hydrated onto the workspace proposal
 * payload by connect-service) and renders each thread as a type badge
 * (Revision Required / Question) + a status pill (Outstanding / Resolved,
 * driven by the thread's own authoritative `status` — no client derivation),
 * with its messages sender-aligned (VENDOR = "You" / right, INTERNAL =
 * "CREtelligent Review Team" / left).
 *
 * The PA can: reply on an OUTSTANDING thread (posts a VENDOR message), open a
 * new QUESTION thread ("Ask a Question" — the PA UI never offers
 * REVISION_REQUIRED; the server forces QUESTION too), and resolve their own
 * QUESTION threads. A REVISION_REQUIRED thread is closed by the review team
 * only, so no resolve control is shown on it.
 *
 * Persistence goes through `ReviewThreadService` (the connect-service PA
 * endpoints), not the generic proposal save. On success each mutation
 * optimistically updates the local `proposal.reviewThreads` and re-emits the
 * `{ siteDetail }` refresh the Documents/Scheduling cards use, so the workspace
 * merges it back through the shared ProjectService subject. On failure the
 * draft is kept and a toast is shown — a lost free-text reply isn't
 * recoverable. The unused `comments-dialog` stub is left in place, untouched.
 */
@Component({
  standalone: true,
  selector: 'app-comments-card',
  imports: [CommonModule, FormsModule, StatusPillComponent],
  templateUrl: './comments-card.component.html',
  styleUrl: './comments-card.component.scss',
})
export class CommentsCardComponent {
  // Server-enforced message length cap (SF-safe, consistent with note/decline).
  // Mirrored here as a maxlength + counter so the PA can't type past it; the
  // server rejects over-255 regardless.
  readonly maxLength = 255;

  // Full siteDetail ({ site, proposal }) — same shape the Documents/Scheduling
  // cards take, so a mutation can be re-emitted as a complete updated
  // siteDetail.
  @Input() siteDetail: any = {};

  // Emits the same `{ siteDetail }` shape the Documents/Scheduling cards emit on
  // `update`, so the workspace's single-site refresh path is reused unchanged.
  @Output() update = new EventEmitter<{ siteDetail: any }>();

  // View-only mode (the /project-history/:id record). Threads and messages still
  // render; the reply boxes, "Ask a Question" and resolve controls are
  // suppressed.
  @Input() readOnly: boolean = false;

  // Draft reply text per thread id — each OUTSTANDING thread gets its own inline
  // reply box.
  replyDrafts: Record<string, string> = {};

  // In-flight flags per thread id — guard against double-submit and drive the
  // Send / Resolve button disabled/label state.
  sending: Record<string, boolean> = {};
  resolving: Record<string, boolean> = {};

  // Per-thread expand/collapse override. Default (no override): OUTSTANDING
  // threads are expanded, RESOLVED threads are collapsed — so the card leads
  // with what still needs attention, mirroring the Order Tracker panel.
  expandedThreads: Record<string, boolean> = {};

  // "Ask a Question" composer state.
  askingQuestion = false;
  newQuestionDraft = '';
  creatingQuestion = false;

  constructor(
    private reviewThreadService: ReviewThreadService,
    private toastr: ToastrService
  ) {}

  get proposal(): any {
    return this.siteDetail?.proposal ?? {};
  }

  get site(): any {
    return this.siteDetail?.site ?? {};
  }

  private get rawThreads(): any[] {
    return this.proposal.reviewThreads ?? [];
  }

  // The connect site id used to create a new thread. Every existing thread
  // carries the same `siteId`; fall back to the site/proposal identifiers when
  // there are none yet.
  private get siteId(): string | undefined {
    return this.rawThreads[0]?.siteId ?? this.site?.id ?? this.proposal?.siteId;
  }

  // Threads ordered OUTSTANDING-first, then most-recently-updated first; each
  // thread's own messages ordered oldest-first. Clones rather than mutating the
  // @Input-derived source arrays in place.
  get threads(): any[] {
    return [...this.rawThreads]
      .map((thread) => ({
        ...thread,
        messages: [...(thread.messages ?? [])].sort(
          (a: any, b: any) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
        ),
      }))
      .sort((a, b) => {
        const aOutstanding = this.isOutstanding(a) ? 0 : 1;
        const bOutstanding = this.isOutstanding(b) ? 0 : 1;
        if (aOutstanding !== bOutstanding) {
          return aOutstanding - bOutstanding;
        }
        const aDate = a.updatedDate ?? a.createdDate ?? 0;
        const bDate = b.updatedDate ?? b.createdDate ?? 0;
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      });
  }

  // Display-only header badge — count of OUTSTANDING threads.
  get outstandingCount(): number {
    return this.rawThreads.filter(
      (thread: any) => thread?.status === 'OUTSTANDING'
    ).length;
  }

  isOutstanding(thread: any): boolean {
    return thread?.status === 'OUTSTANDING';
  }

  isQuestion(thread: any): boolean {
    return thread?.type === 'QUESTION';
  }

  // Expanded when explicitly toggled; otherwise OUTSTANDING → expanded,
  // RESOLVED → collapsed.
  isThreadExpanded(thread: any): boolean {
    const override = this.expandedThreads[thread.id];
    return override !== undefined ? override : this.isOutstanding(thread);
  }

  toggleThreadExpanded(thread: any): void {
    this.expandedThreads[thread.id] = !this.isThreadExpanded(thread);
  }

  // StatusPill's own STATUS_MAP supplies the 'Outstanding'/'Resolved' label for
  // these keys, so no explicit [label] override is needed at the callsite.
  pillStatus(thread: any): string {
    return this.isOutstanding(thread) ? 'outstanding' : 'resolved';
  }

  typeLabel(thread: any): string {
    return this.isQuestion(thread) ? 'Question' : 'Revision Required';
  }

  // Question threads render with the neutral/accent (info) badge; revision
  // threads with the danger badge — matching the mockup.
  typeBadgeClass(thread: any): string {
    return this.isQuestion(thread) ? 'pill pill--info' : 'pill pill--danger';
  }

  messageAuthor(thread: any, message: any): string {
    if (message?.sender === 'VENDOR') {
      return 'You';
    }
    return message?.authorName || 'CREtelligent Review Team';
  }

  isVendorMessage(message: any): boolean {
    return message?.sender === 'VENDOR';
  }

  // Only the PA's own QUESTION threads are resolvable here; REVISION_REQUIRED is
  // closed by the review team (the server 403s the PA on those).
  canResolve(thread: any): boolean {
    return (
      !this.readOnly && this.isOutstanding(thread) && this.isQuestion(thread)
    );
  }

  canSend(thread: any): boolean {
    const text = (this.replyDrafts[thread.id] ?? '').trim();
    return !!text && text.length <= this.maxLength;
  }

  canCreateQuestion(): boolean {
    const text = this.newQuestionDraft.trim();
    return !!text && text.length <= this.maxLength && !!this.siteId;
  }

  toggleAskQuestion(): void {
    this.askingQuestion = !this.askingQuestion;
    if (!this.askingQuestion) {
      this.newQuestionDraft = '';
    }
  }

  // Explicit close for the composer's own Cancel button — always closes
  // (never re-opens) and drops the in-progress draft, mirroring the
  // toggle-closed behavior above.
  cancelQuestion(): void {
    this.askingQuestion = false;
    this.newQuestionDraft = '';
  }

  // ---------------------------------------------------------------------------
  // Post a reply (VENDOR message) on an OUTSTANDING thread. On success the
  // message is optimistically appended and `update` emitted; on a rejected or
  // failed post the typed reply is kept so the PA can retry.
  // ---------------------------------------------------------------------------
  send(thread: any): void {
    if (!this.canSend(thread) || this.sending[thread.id]) {
      return;
    }
    const text = this.replyDrafts[thread.id].trim();

    this.sending[thread.id] = true;
    this.reviewThreadService.postMessage(thread.id, text).subscribe({
      next: (data: any) => {
        this.sending[thread.id] = false;
        const message = this.toMessage(data, text);
        this.applyThreadUpdate(thread.id, (t) => ({
          ...t,
          messages: [...(t.messages ?? []), message],
          updatedDate: message.date,
        }));
        this.replyDrafts[thread.id] = '';
      },
      error: () => {
        this.sending[thread.id] = false;
        this.toastr.error('Your reply could not be sent. Please try again.');
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Open a new QUESTION thread. The server forces `type = QUESTION`; on success
  // it returns the created thread, which is prepended and `update` emitted. On
  // failure the draft is kept.
  // ---------------------------------------------------------------------------
  createQuestion(): void {
    if (!this.canCreateQuestion() || this.creatingQuestion) {
      return;
    }
    const siteId = this.siteId!;
    const text = this.newQuestionDraft.trim();

    this.creatingQuestion = true;
    this.reviewThreadService.createQuestionThread(siteId, text).subscribe({
      next: (data: any) => {
        this.creatingQuestion = false;
        const thread = this.toCreatedThread(data, siteId, text);
        const updatedProposal = {
          ...this.proposal,
          reviewThreads: [...this.rawThreads, thread],
        };
        this.newQuestionDraft = '';
        this.askingQuestion = false;
        this.emitProposal(updatedProposal);
      },
      error: () => {
        this.creatingQuestion = false;
        this.toastr.error(
          'Your question could not be sent. Please try again.'
        );
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Resolve the PA's own QUESTION thread.
  // ---------------------------------------------------------------------------
  resolve(thread: any): void {
    if (!this.canResolve(thread) || this.resolving[thread.id]) {
      return;
    }
    this.resolving[thread.id] = true;
    this.reviewThreadService.resolveThread(thread.id).subscribe({
      next: () => {
        this.resolving[thread.id] = false;
        this.applyThreadUpdate(thread.id, (t) => ({
          ...t,
          status: 'RESOLVED' as ThreadStatus,
          updatedDate: new Date(),
        }));
      },
      error: () => {
        this.resolving[thread.id] = false;
        this.toastr.error(
          'This question could not be resolved. Please try again.'
        );
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  // Applies an immutable transform to one thread by id and emits the refreshed
  // siteDetail through the shared workspace path.
  private applyThreadUpdate(
    threadId: string,
    transform: (thread: any) => any
  ): void {
    const updatedThreads = this.rawThreads.map((t: any) =>
      t.id === threadId ? transform(t) : t
    );
    this.emitProposal({ ...this.proposal, reviewThreads: updatedThreads });
  }

  private emitProposal(updatedProposal: any): void {
    this.update.emit({
      siteDetail: { ...this.siteDetail, proposal: updatedProposal },
    });
  }

  // Prefers the server-returned message; falls back to an optimistic VENDOR
  // message when the endpoint returns no (or a differently-shaped) body.
  private toMessage(data: any, body: string): any {
    if (data && data.body !== undefined && data.sender !== undefined) {
      return data;
    }
    return { body, sender: 'VENDOR', authorName: 'You', date: new Date() };
  }

  // Prefers the server-returned thread; falls back to an optimistic QUESTION
  // thread so the new question renders immediately even if the body is empty.
  private toCreatedThread(data: any, siteId: string, body: string): any {
    if (data && data.id) {
      return data;
    }
    const now = new Date();
    return {
      id: `pending-${now.getTime()}`,
      siteId,
      type: 'QUESTION' as ThreadType,
      status: 'OUTSTANDING' as ThreadStatus,
      createdBy: 'VENDOR',
      createdDate: now,
      updatedDate: now,
      messages: [{ body, sender: 'VENDOR', authorName: 'You', date: now }],
    };
  }
}
